import { api } from '@/api/client';

// Three types only (Igor): hourly work (waiting OR an unpriced task — billed per hour),
// kilometervergoeding (server-priced per km), and a free-form catch-all. The `wachturen`
// storage key is kept (relabelled "Werkuren") so existing hourly records stay valid.
// Older records may still carry retired keys (voertuigvergoeding, …) — display falls back
// to the raw key; they're simply no longer selectable when creating a new meerwerk.
export type MeerwerkType =
  | 'wachturen'
  | 'kilometervergoeding'
  | 'overig'
  // An onderaannemer's own binddraad, counted in rollen. Booked on his PO without the
  // project analytic (the materiaallijst already books the estimate on the project).
  | 'binddraad';

export type MeerwerkUnit = 'hours' | 'pieces' | 'lump_sum' | 'km';
export type MeerwerkStatus = 'pending' | 'on_po' | 'dismissed';

export const MEERWERK_TYPE_LABELS: Record<MeerwerkType, string> = {
  wachturen: 'Werkuren',
  kilometervergoeding: 'Kilometervergoeding',
  overig: 'Overig',
  binddraad: 'Binddraad',
};

export const MEERWERK_UNIT_LABELS: Record<MeerwerkUnit, string> = {
  hours: 'Uren',
  pieces: 'Stuks',
  lump_sum: 'Vast bedrag',
  km: 'Kilometer',
};

export const MEERWERK_STATUS_LABELS: Record<MeerwerkStatus, string> = {
  pending: 'Open',
  on_po: 'Op PO',
  dismissed: 'Afgewezen',
};

// English labels — used in installer-facing views (subcontractors prefer EN).
// Storage values are unchanged; only display differs from Dutch admin labels.
export const MEERWERK_TYPE_LABELS_EN: Record<MeerwerkType, string> = {
  wachturen: 'Work hours',
  kilometervergoeding: 'Mileage',
  overig: 'Other',
  binddraad: 'Binding wire',
};

export const MEERWERK_UNIT_LABELS_EN: Record<MeerwerkUnit, string> = {
  hours: 'Hours',
  pieces: 'Pieces',
  lump_sum: 'Lump sum',
  km: 'Km',
};

export const MEERWERK_STATUS_LABELS_EN: Record<MeerwerkStatus, string> = {
  pending: 'Open',
  on_po: 'On PO',
  dismissed: 'Dismissed',
};

export interface Meerwerk {
  id: number;
  name: string;
  type: MeerwerkType;
  unit: MeerwerkUnit | null;
  quantity: number;
  unit_price: number;
  /** The price that will actually be billed. For km meerwerken the server fills in
   *  the €/km price of the Odoo km product for the vendor (unit_price is 0); for all
   *  others it equals unit_price. null = no price: the km line would be refused, and
   *  price_unavailable_reason says why. Never fall back to unit_price for a null. */
  effective_unit_price: number | null;
  /** Server's Dutch reason when effective_unit_price is null; null otherwise. */
  price_unavailable_reason?: string | null;
  note: string;
  created_by: string;
  status: MeerwerkStatus;
  date: string;
  project_id: number | null;
  /** Human werf name, populated by the OA-dagoverzicht so a row shows its project.
   *  Optional — the plain meerwerken list endpoint doesn't resolve it. */
  project_name?: string | null;
  subcontractor_id: number | null;
  subcontractor_name: string | null;
  task_id: number | null;
  task_name: string | null;
  /** Server-derived: the linked task is an interventie (Odoo `Interventie` tag, self or fase). */
  task_is_interventie?: boolean;
  purchase_order_id: number | null;
  purchase_order_name: string | null;
}

export interface CreateMeerwerkPayload {
  name: string;
  type: MeerwerkType;
  unit?: MeerwerkUnit;
  quantity?: number;
  unit_price?: number;
  note?: string;
  date?: string;
  task_id?: number;
  // Admin-only: create a meerwerk on behalf of a subcontractor (InkoopordersTab flow).
  subcontractor_id?: number;
}

export interface PatchMeerwerkPayload {
  name?: string;
  type?: MeerwerkType;
  unit?: MeerwerkUnit;
  quantity?: number;
  unit_price?: number;
  note?: string;
  date?: string;
}

/** Prices the create forms prefill. Null when Thermoduct has not set one in Odoo. */
export interface MeerwerkDefaults {
  binddraad_unit_price: number | null;
}

export async function getMeerwerkDefaults(): Promise<MeerwerkDefaults> {
  return api<MeerwerkDefaults>('/api/meerwerken/defaults');
}

export async function listProjectMeerwerken(
  projectId: number,
  filters?: { status?: MeerwerkStatus; subcontractorId?: number; taskId?: number },
): Promise<Meerwerk[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.subcontractorId) params.set('subcontractorId', String(filters.subcontractorId));
  if (filters?.taskId) params.set('taskId', String(filters.taskId));
  const qs = params.toString();
  return api<Meerwerk[]>(`/api/projects/${projectId}/meerwerken${qs ? `?${qs}` : ''}`);
}

export async function createMeerwerk(
  projectId: number,
  payload: CreateMeerwerkPayload,
): Promise<{ id: number; task_id: number | null }> {
  return api<{ id: number; task_id: number | null }>(
    `/api/projects/${projectId}/meerwerken`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function patchMeerwerk(
  id: number,
  payload: PatchMeerwerkPayload,
): Promise<{ id: number; updated: boolean }> {
  return api<{ id: number; updated: boolean }>(
    `/api/meerwerken/${id}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  );
}

export async function deleteMeerwerk(id: number): Promise<{ id: number; deleted: boolean }> {
  return api<{ id: number; deleted: boolean }>(
    `/api/meerwerken/${id}`,
    { method: 'DELETE' },
  );
}
